import { useState, useEffect } from 'react';
import { 
  getAllowedFileTypes, 
  updateAllowedFileTypes,
  getGeneralSettings,
  updateGeneralSettings
} from '../../services/systemSettingsService';
import { getAllUsers, getUserApplications } from '../../services/userService';

export default function SystemSettingsLogic() {
  const [allowedFileTypes, setAllowedFileTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserApplications, setSelectedUserApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [generalSettings, setGeneralSettings] = useState({
    systemName: 'App Analysis System',
    maxStoragePerUser: 10,
    systemVersion: '1.0.0'
  });
  
  // State for adding new file type
  const [newFileType, setNewFileType] = useState({
    extension: '',
    displayName: '',
    mimeType: '',
    maxSize: 100 * 1024 * 1024, // 100MB default
    enabled: true
  });
  
  // Load settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Load file types
        const fileTypes = await getAllowedFileTypes();
        setAllowedFileTypes(fileTypes);
        
        // Load general settings
        const settings = await getGeneralSettings();
        setGeneralSettings(settings);
      } catch (error) {
        console.error('Error loading settings:', error);
        setError('Failed to load system settings');
      } finally {
        setLoading(false);
      }
    };
    
    loadSettings();
  }, []);
  
  // Load users on component mount
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setUsersLoading(true);
        const usersData = await getAllUsers();
        setUsers(usersData);
      } catch (error) {
        console.error('Error loading users:', error);
        // Don't set global error for users to avoid confusion with file types error
      } finally {
        setUsersLoading(false);
      }
    };
    
    loadUsers();
  }, []);

  // Load user applications when a user is selected
  useEffect(() => {
    const loadUserApplications = async () => {
      if (!selectedUser) return;

      console.log('Loading applications for selected user:', selectedUser);
      try {
        setApplicationsLoading(true);
        const applications = await getUserApplications(selectedUser.uid);
        console.log('Loaded applications:', applications);
        setSelectedUserApplications(applications);
      } catch (error) {
        console.error('Error loading user applications:', error);
        setError('Failed to load user applications');
      } finally {
        setApplicationsLoading(false);
      }
    };

    loadUserApplications();
  }, [selectedUser]);
  
  // Handle general settings update
  const handleGeneralSettingsUpdate = async (field, value) => {
    try {
      const updatedSettings = {
        ...generalSettings,
        [field]: value
      };
      
      // Save to Firebase
      await updateGeneralSettings(updatedSettings);
      
      // Update local state
      setGeneralSettings(updatedSettings);
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      setError('');
    } catch (error) {
      console.error('Error updating general settings:', error);
      setError('Failed to update settings');
    }
  };
  
  // Handle input change for new file type form
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewFileType(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Add new file type
  const handleAddFileType = async () => {
    // Validate inputs
    if (!newFileType.extension || !newFileType.displayName) {
      setError('Extension and Display Name are required');
      return;
    }
    
    // Format extension to start with a dot if not already
    const formattedExtension = newFileType.extension.startsWith('.') 
      ? newFileType.extension 
      : `.${newFileType.extension}`;
    
    // Check if this extension already exists
    if (allowedFileTypes.some(type => type.extension.toLowerCase() === formattedExtension.toLowerCase())) {
      setError(`File type ${formattedExtension} already exists`);
      return;
    }
    
    try {
      // Add to the current state
      const newFileTypeFormatted = {
        ...newFileType,
        extension: formattedExtension,
        maxSize: Number(newFileType.maxSize) || 100 * 1024 * 1024
      };
      
      const updatedFileTypes = [...allowedFileTypes, newFileTypeFormatted];
      
      // Save to Firebase
      await updateAllowedFileTypes(updatedFileTypes);
      
      // Update local state
      setAllowedFileTypes(updatedFileTypes);
      
      // Reset form
      setNewFileType({
        extension: '',
        displayName: '',
        mimeType: '',
        maxSize: 100 * 1024 * 1024,
        enabled: true
      });
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      setError('');
    } catch (error) {
      console.error('Error adding file type:', error);
      setError('Failed to add file type');
    }
  };
  
  // Toggle file type enabled/disabled
  const handleToggleFileType = async (index) => {
    try {
      const updatedFileTypes = [...allowedFileTypes];
      updatedFileTypes[index].enabled = !updatedFileTypes[index].enabled;
      
      // Save to Firebase
      await updateAllowedFileTypes(updatedFileTypes);
      
      // Update local state
      setAllowedFileTypes(updatedFileTypes);
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error toggling file type:', error);
      setError('Failed to update file type');
    }
  };
  
  // Delete file type
  const handleDeleteFileType = async (index) => {
    try {
      // Don't allow deleting the last file type
      if (allowedFileTypes.length <= 1) {
        setError('Cannot delete the last file type');
        return;
      }
      
      const updatedFileTypes = allowedFileTypes.filter((_, i) => i !== index);
      
      // Save to Firebase
      await updateAllowedFileTypes(updatedFileTypes);
      
      // Update local state
      setAllowedFileTypes(updatedFileTypes);
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      setError('');
    } catch (error) {
      console.error('Error deleting file type:', error);
      setError('Failed to delete file type');
    }
  };
  
  // Update existing file type
  const handleUpdateFileType = async (index, updatedData) => {
    try {
      const updatedFileTypes = [...allowedFileTypes];
      updatedFileTypes[index] = {
        ...updatedFileTypes[index],
        ...updatedData
      };
      
      // Save to Firebase
      await updateAllowedFileTypes(updatedFileTypes);
      
      // Update local state
      setAllowedFileTypes(updatedFileTypes);
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      setError('');
    } catch (error) {
      console.error('Error updating file type:', error);
      setError('Failed to update file type');
    }
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    console.log('User selected:', user);
    setSelectedUser(user);
  };

  // Handle modal close
  const handleModalClose = () => {
    console.log('Closing modal');
    setSelectedUser(null);
    setSelectedUserApplications([]);
  };
  
  return {
    allowedFileTypes,
    users,
    loading,
    usersLoading,
    error,
    newFileType,
    generalSettings,
    selectedUser,
    selectedUserApplications,
    applicationsLoading,
    handleGeneralSettingsUpdate,
    handleInputChange,
    handleAddFileType,
    handleToggleFileType,
    handleDeleteFileType,
    handleUpdateFileType,
    handleUserSelect,
    handleModalClose,
    saveSuccess
  };
} 